import { useState } from "react";
import { Button, Modal, NumberInput } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import {
  UnsavedChangesPrompt,
  useDiscardGuard,
} from "@/shared/UnsavedChangesPrompt";

/**
 * How many people are at this table (B6, D45).
 *
 * Covers used to be answerable only on the floor screen, in the moment before
 * the first menu tap — and the till opens the bill ON that tap, so the single
 * moment the question existed was the moment a waiter is least likely to have
 * counted heads. There was no later path: `guestCount` was written by
 * `openInvoice` and by nothing else. `coverCoverage` then read near zero,
 * honestly, on a report the owner had paid for.
 *
 * So the question moved to where the answer is known. Two things follow from
 * that, and both are why this is a dialog rather than an inline field:
 *
 * - CLEARING has to be possible. A mis-typed 8 that can never be taken back is
 *   worse for the cover average than no number at all, so "clear" is its own
 *   button sending an explicit `null`, not an empty box the waiter has to
 *   guess is saved.
 * - Blank must stay a real answer. The input opens on the count already on the
 *   bill, or EMPTY when nobody has answered — never on a helpful "1", which
 *   would put an invented cover on every bill a waiter opened and closed
 *   without ever being asked.
 */
export function GuestCountDialog({
  current,
  isSubmitting,
  onCancel,
  onConfirm,
  title,
  hint,
}: {
  /** What the bill already carries; `null` when nobody has counted. */
  current: number | null;
  isSubmitting: boolean;
  onCancel: () => void;
  /** `null` means "nobody counted after all" — a real answer, not a cancel. */
  onConfirm: (guestCount: number | null) => void | Promise<void>;
  /**
   * Overrides the default title/hint — used when this dialog is opened FROM
   * THE CLOSE PATH (covers rework, Phase 1), which asks a slightly different
   * question ("how many people WAS the table", past tense, closing out a
   * service) than the ordinary edit path opened from the covers row itself.
   * The normal edit path passes neither and keeps its existing copy.
   */
  title?: string;
  hint?: string;
}) {
  const t = useTranslate();
  const [value, setValue] = useState(current === null ? "" : String(current));
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed === "") {
      // An empty box on SAVE is the same statement the Clear button makes.
      // Refusing it here would mean the only way to say "I don't know" is a
      // button the waiter may not have noticed.
      void onConfirm(null);
      return;
    }
    const parsed = Number(trimmed);
    // The same bounds the server enforces, so the common typo is answered here
    // instead of as a round trip that says the same thing in English.
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 500) {
      setError(t((m) => m.pos.guestCountInvalid));
      return;
    }
    setError(null);
    void onConfirm(parsed);
  };

  // A typed count that is not the one on the bill — including a box emptied
  // deliberately, which is the «سيبه فاضي» answer and a real one.
  const guard = useDiscardGuard(
    value.trim() !== (current === null ? "" : String(current)),
    onCancel,
  );

  return (
    <>
    <Modal
      isOpen
      onClose={guard.requestClose}
      size="sm"
      title={title ?? t((m) => m.pos.guestCountDialogTitle)}
      closeLabel={t((m) => m.common.close)}
      data-testid="guest-count-dialog"
      footerActions={
        <>
          <Button variant="outline" size="touch" onClick={guard.requestClose}>
            {t((m) => m.common.cancel)}
          </Button>
          {/* Only when there is something to clear — a Clear on a bill nobody
              has counted is a button that does nothing. */}
          {current !== null ? (
            <Button
              variant="outline"
              size="touch"
              isLoading={isSubmitting}
              data-testid="guest-count-clear"
              onClick={() => void onConfirm(null)}
            >
              {t((m) => m.pos.guestCountClear)}
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="touch"
            className="font-extrabold"
            isLoading={isSubmitting}
            data-testid="guest-count-confirm"
            onClick={submit}
          >
            {t((m) => m.common.save)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label
          htmlFor="guest-count-amount"
          className="text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-secondary)]"
        >
          {t((m) => m.pos.guestCountLabel)}
        </label>
        <NumberInput
          id="guest-count-amount"
          name="guestCount"
          value={value}
          min={1}
          max={500}
          dir="ltr"
          hasError={Boolean(error)}
          placeholder={t((m) => m.pos.guestCountPlaceholder)}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          data-testid="guest-count-amount"
        />
        <p className="text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]">
          {hint ?? t((m) => m.pos.guestCountDialogHint)}
        </p>
        {error ? (
          <p
            role="alert"
            className="text-xs font-medium text-[color:var(--danger)]"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
    <UnsavedChangesPrompt
      isOpen={guard.asking}
      onDiscard={guard.discard}
      onKeepEditing={guard.keepEditing}
      testId="guest-count-discard-prompt"
    />
    </>
  );
}
